// White-label theming for report variants.
//
// A theme carries only the per-client brand values. Everything that must stay
// visually identical across clients — the clinical severity scale and the page
// chrome — lives here as a frozen constant so a second brand cannot fork it.

export type WhiteLabelPageKey =
  | "coverResults"
  | "caloricNutrition"
  | "nutritionGuide"
  | "movementPlan"
  | "sourcesDisclaimer";

export const DEFAULT_WHITE_LABEL_PAGE_ORDER: WhiteLabelPageKey[] = [
  "coverResults",
  "caloricNutrition",
  "nutritionGuide",
  "movementPlan",
  "sourcesDisclaimer",
];

export type WhiteLabelTheme = {
  /** Matches data.customType coming off the report payload. */
  customType: string;
  /** Substituted wherever the default report says "PNOE" (e.g. the disclaimer). */
  companyName: string;
  contactEmail: string;
  /** Imported image asset (webpack resolves this to a URL). */
  logo: string;
  /** Rendered logo height in px. Per-brand because lockup proportions differ. */
  logoHeight: number;
  /**
   * Brand QR code. Required, because the payload's QRCODE points at
   * pnoe.com/blog — correct for a PNOE report, wrong on a white-labelled one.
   */
  qrCode: string;
  colors: {
    primary: string;
    accentA: string;
    accentB: string;
  };
  /** Movement Plan category markers. */
  sectionMarkers: {
    yoga: string;
    aerobic: string;
    strength: string;
  };
  /** Overrides the GeneratePDF filename stem. */
  pdfFileName?: string;
  pageOrder?: WhiteLabelPageKey[];
};

/**
 * Fixed clinical severity scale — NOT themeable, identical for every client.
 *
 * Boundaries mirror ChartBars.tsx (<=20 / <=40 / <=60 / <=80) so a given score
 * lands in the same band here as on every other PNOE surface. The printed range
 * labels live in the i18n dictionary and must be kept in step with these numbers.
 */
export const SEVERITY_SCALE = [
  { key: "severe", max: 20, color: "#E2635B" },
  { key: "limitation", max: 40, color: "#EFA33E" },
  { key: "neutral", max: 60, color: "#4E8FC7" },
  { key: "good", max: 80, color: "#5FC0B6" },
  { key: "excellent", max: 100, color: "#128F84" },
] as const;

export type SeverityKey = (typeof SEVERITY_SCALE)[number]["key"];

export const severityForValue = (value: number) =>
  SEVERITY_SCALE.find((band) => value <= band.max) ??
  SEVERITY_SCALE[SEVERITY_SCALE.length - 1];

/**
 * Fixed tokens needed from TypeScript rather than CSS.
 *
 * SVG presentation attributes do not substitute `var()`, so Recharts props must
 * receive literal hex. Everything else in the structural palette is declared in
 * `pages/whiteLabel/WhiteLabelPage/WhiteLabelPage.module.css` — keep `ruleStrong`
 * in step with the `--wl-rule-strong` value there.
 */
export const FIXED_TOKENS = {
  ruleStrong: "#C7D1D5",
  /** Macronutrient triangle fill is theme-agnostic per the PRD. */
  triangleFill: "#4AA8DA",
} as const;
